// ===== ANONYMOUS AI v25.0 QUANTUM - WEB SEARCH SERVICE =====

import axios from 'axios';

class WebSearchService {
  constructor() {
    this.serperKey = process.env.REACT_APP_SERPER_API_KEY;
    this.braveKey = process.env.REACT_APP_BRAVE_API_KEY;
    this.tavilyKey = process.env.REACT_APP_TAVILY_API_KEY;
  }

  /**
   * Search web with automatic provider fallback
   */
  async search(query, options = {}) {
    const {
      numResults = 10,
      freshness = 'all', // 'day', 'week', 'month', 'year', 'all'
      safeSearch = false
    } = options;

    // Try providers in order
    const providers = [
      { name: 'Serper', fn: () => this.searchSerper(query, numResults) },
      { name: 'Brave', fn: () => this.searchBrave(query, numResults, freshness) },
      { name: 'Tavily', fn: () => this.searchTavily(query, numResults) },
      { name: 'DuckDuckGo', fn: () => this.searchDuckDuckGo(query, numResults) }
    ];

    for (const provider of providers) {
      try {
        console.log(`🔍 Trying ${provider.name} for search...`);
        const results = await provider.fn();
        
        if (results && results.length > 0) {
          console.log(`✅ ${provider.name} returned ${results.length} results`);
          return {
            query,
            results,
            provider: provider.name,
            timestamp: Date.now()
          };
        }
      } catch (error) {
        console.warn(`❌ ${provider.name} failed:`, error.message);
        continue;
      }
    }

    throw new Error('All search providers failed');
  }

  /**
   * Serper.dev search (best quality)
   */
  async searchSerper(query, numResults) {
    if (!this.serperKey) throw new Error('Serper API key not configured');

    const response = await axios.post(
      'https://google.serper.dev/search',
      {
        q: query,
        num: numResults,
        gl: 'us',
        hl: 'en'
      },
      {
        headers: {
          'X-API-KEY': this.serperKey,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      }
    );

    return (response.data.organic || []).map(result => ({
      title: result.title,
      url: result.link,
      snippet: result.snippet,
      date: result.date || null
    }));
  }

  /**
   * Brave Search API
   */
  async searchBrave(query, numResults, freshness) {
    if (!this.braveKey) throw new Error('Brave API key not configured');

    const params = {
      q: query,
      count: numResults,
      text_decorations: false,
      search_lang: 'en'
    };

    if (freshness && freshness !== 'all') {
      params.freshness = freshness;
    }

    const response = await axios.get(
      'https://api.search.brave.com/res/v1/web/search',
      {
        params,
        headers: {
          'Accept': 'application/json',
          'X-Subscription-Token': this.braveKey
        },
        timeout: 10000
      }
    );

    return (response.data.web?.results || []).map(result => ({
      title: result.title,
      url: result.url,
      snippet: result.description,
      date: result.age || null
    }));
  }

  /**
   * Tavily AI Search (optimized for LLMs)
   */
  async searchTavily(query, numResults) {
    if (!this.tavilyKey) throw new Error('Tavily API key not configured');

    const response = await axios.post(
      'https://api.tavily.com/search',
      {
        api_key: this.tavilyKey,
        query,
        max_results: numResults,
        search_depth: 'basic',
        include_answer: false,
        include_raw_content: false
      },
      {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000
      }
    );

    return (response.data.results || []).map(result => ({
      title: result.title,
      url: result.url,
      snippet: result.content,
      date: result.published_date || null
    }));
  }

  /**
   * DuckDuckGo scraping (fallback, no API key needed)
   */
  async searchDuckDuckGo(query, numResults) {
    // Using DuckDuckGo Instant Answer API (limited)
    const response = await axios.get(
      'https://api.duckduckgo.com/',
      {
        params: {
          q: query,
          format: 'json',
          no_redirect: 1,
          no_html: 1,
          skip_disambig: 1
        },
        timeout: 10000
      }
    );

    const results = [];

    // Abstract
    if (response.data.Abstract) {
      results.push({
        title: response.data.Heading || query,
        url: response.data.AbstractURL,
        snippet: response.data.Abstract,
        date: null
      });
    }

    // Related topics
    if (response.data.RelatedTopics) {
      response.data.RelatedTopics.slice(0, numResults - 1).forEach(topic => {
        if (topic.FirstURL && topic.Text) {
          results.push({
            title: topic.Text.split(' - ')[0],
            url: topic.FirstURL,
            snippet: topic.Text,
            date: null
          });
        }
      });
    }

    return results;
  }

  /**
   * Fetch full webpage content
   */
  async fetchPage(url) {
    try {
      const response = await axios.get(url, {
        timeout: 15000,
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });

      // Basic text extraction (in production, use cheerio)
      const text = response.data
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      return text.substring(0, 5000); // Limit to 5000 chars
    } catch (error) {
      console.error('Failed to fetch page:', error.message);
      return null;
    }
  }

  /**
   * Search and summarize
   */
  async searchAndSummarize(query, apiCallFunction) {
    try {
      const searchResults = await this.search(query, { numResults: 5 });
      
      // Build context from search results
      const context = searchResults.results
        .map((r, i) => `[${i + 1}] ${r.title}\n${r.snippet}\nURL: ${r.url}`)
        .join('\n\n');

      // Ask AI to synthesize
      const prompt = `Based on these web search results, provide a comprehensive answer:

${context}

Question: ${query}

Provide a detailed answer citing the sources [1], [2], etc.`;

      const response = await apiCallFunction(prompt);
      
      return {
        answer: response.content,
        sources: searchResults.results,
        query
      };
    } catch (error) {
      console.error('Search and summarize failed:', error);
      throw error;
    }
  }

  /**
   * Get news for a topic
   */
  async getNews(topic, daysBack = 7) {
    const dateFilter = daysBack === 1 ? 'day' : 
                       daysBack === 7 ? 'week' : 
                       'month';

    return this.search(`${topic} news`, {
      numResults: 10,
      freshness: dateFilter
    });
  }

  /**
   * Fact check a claim
   */
  async factCheck(claim) {
    const searchQuery = `fact check: ${claim}`;
    return this.search(searchQuery, { numResults: 5 });
  }
}

export default new WebSearchService();
